import React, { useState } from "react";
import {
  TextField,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  CircularProgress,
  Snackbar,
  Alert,
  useMediaQuery,
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Container,
  Breadcrumbs,
  Link,
  Avatar,
  Divider,
  Fade,
  Zoom,
  alpha,
  Tooltip,
} from "@mui/material";
import { 
  Visibility, 
  VisibilityOff,
  Person,
  Email,
  Phone,
  Lock,
  Restaurant,
  LocationOn,
  CheckCircle,
  ArrowBack,
  Home,
  PersonAdd,
  Security,
  Info,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  setDoc,
  doc,
  or,
} from "firebase/firestore";
import { db } from "../firebase-config";
import Collections from "../collections";
import LOGO from "../assets/MF-CPU-LOGO.png";
import Header from "../components/header";

const AddUsers = () => {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    restaurantName: "",
    address: "",
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const isMobile = useMediaQuery("(max-width:600px)");
  const isTablet = useMediaQuery("(max-width:960px)");

  const steps = ['Personal Information', 'Account Details', 'Restaurant Information'];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    // Clear error for this field when user starts typing
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: "",
      });
    }
  };

  // Regular expression to validate UK phone number
  const isValidUKPhone = (phone) => {
    const ukPhoneRegex = /^(\+44\s?7\d{3}|\(?07\d{3}\)?|\d{5}|\d{4})\s?\d{3,4}\s?\d{3,4}$/;
    return ukPhoneRegex.test(phone);
  };

  const validateStep = (step) => {
    let stepErrors = {};

    switch(step) {
      case 0: // Personal Information
        if (!formData.name) stepErrors.name = "Name is required";
        if (!formData.email) stepErrors.email = "Email is required";
        else if (!/\S+@\S+\.\S+/.test(formData.email))
          stepErrors.email = "Email is invalid";
        if (!formData.phone) stepErrors.phone = "Phone number is required";
        else if (!isValidUKPhone(formData.phone))
          stepErrors.phone = "Invalid UK phone number format";
        break;
      
      case 1: // Account Details
        if (!formData.password) stepErrors.password = "Password is required";
        else if (formData.password.length < 8)
          stepErrors.password = "Password must be at least 8 characters";
        if (!formData.confirmPassword)
          stepErrors.confirmPassword = "Confirm password is required";
        else if (formData.password !== formData.confirmPassword)
          stepErrors.confirmPassword = "Passwords do not match";
        break;
      
      case 2: // Restaurant Information
        if (!formData.restaurantName)
          stepErrors.restaurantName = "Restaurant name is required";
        if (!formData.address) stepErrors.address = "Address is required";
        break;
      
      default:
        break;
    }

    return stepErrors;
  };

  const handleNext = () => {
    const stepErrors = validateStep(activeStep);
    
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
    } else {
      setErrors({});
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
    setErrors({});
  };

  // Check if a user with the same email or phone already exists
  const checkIfUserExists = async (email, phone) => {
    const q = query(
      collection(db, Collections.USERS),
      or(where("email", "==", email), where("phone", "==", phone))
    );
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all steps before final submission
    const allErrors = {
      ...validateStep(0),
      ...validateStep(1),
      ...validateStep(2),
    };

    if (Object.keys(allErrors).length > 0) {
      setErrors(allErrors);
      // Find the first step with errors and navigate to it
      if (allErrors.name || allErrors.email || allErrors.phone) {
        setActiveStep(0);
      } else if (allErrors.password || allErrors.confirmPassword) {
        setActiveStep(1);
      } else {
        setActiveStep(2);
      }
    } else {
      setErrors({});
      setLoading(true);

      try {
        const userExists = await checkIfUserExists(formData.email, formData.phone);
        if (userExists) {
          setToast({
            open: true,
            message: "User with the same email or phone already exists",
            severity: "error",
          });
          setLoading(false);
          return;
        }

        const newUser = {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          password: formData.password,
          restaurantName: formData.restaurantName,
          address: formData.address,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          status: "active",
        };

        const newDocRef = doc(collection(db, Collections.USERS));
        await setDoc(newDocRef, { ...newUser, id: newDocRef.id });

        setFormData({
          name: "",
          email: "",
          phone: "",
          password: "",
          confirmPassword: "",
          restaurantName: "",
          address: "",
        });

        setActiveStep(0); // Reset to first step
        setToast({
          open: true,
          message: "User added successfully!",
          severity: "success",
        });
      } catch (error) {
        console.error("Error adding user: ", error);
        setToast({
          open: true,
          message: "Failed to add user",
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Fade in={true} timeout={500}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ color: theme.palette.primary.main, fontWeight: 600 }}>
                  <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Personal Information
                </Typography>
                <Divider sx={{ mb: 3 }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Full Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  fullWidth
                  error={!!errors.name}
                  helperText={errors.name}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person sx={{ color: theme.palette.primary.main }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  fullWidth
                  error={!!errors.email}
                  helperText={errors.email}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email sx={{ color: theme.palette.primary.main }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Phone Number (UK)"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  fullWidth
                  error={!!errors.phone}
                  helperText={errors.phone}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Phone sx={{ color: theme.palette.primary.main }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Fade>
        );
      
      case 1:
        return (
          <Fade in={true} timeout={500}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ color: theme.palette.primary.main, fontWeight: 600 }}>
                  <Security sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Account Security
                </Typography>
                <Divider sx={{ mb: 3 }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  fullWidth
                  error={!!errors.password}
                  helperText={errors.password || "Minimum 8 characters"}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: theme.palette.primary.main }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{ color: theme.palette.text.secondary }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Confirm Password"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  fullWidth
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: theme.palette.primary.main }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          sx={{ color: theme.palette.text.secondary }}
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Fade>
        );
      
      case 2:
        return (
          <Fade in={true} timeout={500}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ color: theme.palette.primary.main, fontWeight: 600 }}>
                  <Restaurant sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Restaurant Details
                </Typography>
                <Divider sx={{ mb: 3 }} />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Restaurant Name"
                  name="restaurantName"
                  value={formData.restaurantName}
                  onChange={handleChange}
                  fullWidth
                  error={!!errors.restaurantName}
                  helperText={errors.restaurantName}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Restaurant sx={{ color: theme.palette.primary.main }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Restaurant Address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  rows={3}
                  error={!!errors.address}
                  helperText={errors.address}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                        <LocationOn sx={{ color: theme.palette.primary.main }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Fade>
        );
      
      default:
        return 'Unknown step';
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.2)} 0%, ${alpha(theme.palette.secondary.light, 0.2)} 100%)`,
      pb: 4,
    }}>
      <Header title="Add User" />
      
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            href="/"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, textDecoration: 'none', color: theme.palette.text.secondary }}
          >
            <Home sx={{ fontSize: 18 }} />
            Home
          </Link>
          <Link
            href="/manage-users"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, textDecoration: 'none', color: theme.palette.text.secondary }}
          >
            <Person sx={{ fontSize: 18 }} />
            Users
          </Link>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PersonAdd sx={{ fontSize: 18 }} />
            Add New User
          </Box>
        </Breadcrumbs>

        {/* Header Card */}
        <Card 
          elevation={0}
          sx={{ 
            mb: 4, 
            borderRadius: 3,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography variant="h4" fontWeight={600} gutterBottom>
                  Add New User
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  Create a new user account with restaurant access
                </Typography>
              </Grid>
              <Grid item xs={12} md={4} sx={{ textAlign: { md: 'right' } }}>
                <Avatar
                  sx={{
                    bgcolor: alpha('#fff', 0.2),
                    width: 80,
                    height: 80,
                    display: 'inline-flex',
                  }}
                >
                  <PersonAdd sx={{ fontSize: 40 }} />
                </Avatar>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Main Form Card */}
        <Paper 
          elevation={0}
          sx={{ 
            borderRadius: 3,
            overflow: 'hidden',
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          {/* Stepper */}
          <Box sx={{ 
            p: 4, 
            pb: 2,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            background: alpha(theme.palette.primary.light, 0.05),
          }}>
            <Stepper 
              activeStep={activeStep} 
              alternativeLabel={isMobile}
              sx={{
                '& .MuiStepLabel-root .Mui-completed': {
                  color: theme.palette.success.main,
                },
                '& .MuiStepLabel-root .Mui-active': {
                  color: theme.palette.primary.main,
                },
              }}
            >
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          {/* Form Content */}
          <Box sx={{ p: 4 }}>
            <form onSubmit={handleSubmit}>
              {getStepContent(activeStep)}

              {/* Navigation Buttons */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                mt: 4,
                pt: 3,
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}>
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  disabled={activeStep === 0}
                  startIcon={<ArrowBack />}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    '&:hover': {
                      borderColor: theme.palette.primary.dark,
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    },
                  }}
                >
                  Back
                </Button>
                
                {activeStep === steps.length - 1 ? (
                  <Button
                    variant="contained"
                    type="submit"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
                    sx={{
                      borderRadius: 2,
                      px: 4,
                      py: 1,
                      background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
                      color: 'white',
                      '&:hover': {
                        background: `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.primary.main} 90%)`,
                      },
                    }}
                  >
                    {loading ? 'Creating...' : 'Create User'}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    sx={{
                      borderRadius: 2,
                      px: 4,
                      py: 1,
                      background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
                      color: 'white',
                      '&:hover': {
                        background: `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.primary.main} 90%)`,
                      },
                    }}
                  >
                    Next
                  </Button>
                )}
              </Box>
            </form>
          </Box>

          {/* Info Footer */}
          <Box sx={{ 
            p: 2, 
            backgroundColor: alpha(theme.palette.info.light, 0.1),
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}>
            <Info sx={{ fontSize: 18, color: theme.palette.info.main }} />
            <Typography variant="caption" color="text.secondary">
              All fields are required. Passwords must be at least 8 characters long.
            </Typography>
          </Box>
        </Paper>

        {/* Toast Notification */}
        <Snackbar
          open={toast.open}
          autoHideDuration={4000}
          onClose={() => setToast({ ...toast, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          TransitionComponent={Fade}
        >
          <Alert
            onClose={() => setToast({ ...toast, open: false })}
            severity={toast.severity}
            variant="filled"
            sx={{ 
              width: '100%',
              borderRadius: 2,
              boxShadow: theme.shadows[8],
            }}
          >
            {toast.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default AddUsers;